<template>
  <div class="navigation">
    <attendance-nav
                    :activeItem="navPanel && navPanel.item"
                    :showPollsItem="activePoll || sharedPollResults"
                    @click-item="clickNavItem">
    </attendance-nav>

    <attendance-nav-panel v-show="navPanel"
                          :title="navPanel && navPanel.title">
      <keep-alive>
        <component :is="navPanel && navPanel.component"
                   @chat-input-focus="(focused) => { $emit('chat-input-focus', focused) }">
        </component>
      </keep-alive>
    </attendance-nav-panel>
  </div>
</template>

<script>
import Chat from '@/components/chat/chat';
import WebinarInfo from '@/components/webinar-info';
import HandoutsList from '@/components/handouts/handouts-list';
import Polls from '@/components/polls/polls';
import windowUtils from '@/utils/window-utils';
import AttendanceNav from './attendance-nav';
import AttendanceNavPanel from './attendance-nav-panel';
import { SESSION_STATUS } from '../../store/modules/g2w-session';

const navPanels = {
  info: {item: 'info', title: 'attendance.nav.info', component: 'WebinarInfo'},
  chat: {item: 'chat', title: 'attendance.nav.chat', component: 'Chat'},
  polls: {item: 'polls', title: 'attendance.nav.polls', component: 'Polls'},
  handouts: {item: 'handouts', title: 'attendance.nav.handouts', component: 'HandoutsList'},
  settings: {item: 'settings', title: 'attendance.nav.settings'}
};

const Navigation = {
  name: 'Navigation',

  components: {
    AttendanceNav,
    AttendanceNavPanel,
    Chat,
    WebinarInfo,
    HandoutsList,
    Polls
  },

  created() {
    // equivalent of layout-vertical mixin
    this.mediaQueryLayoutVertical = windowUtils.matchMedia('screen and (orientation: portrait)');
  },

  mounted() {
    if (this.mediaQueryLayoutVertical) {
      this.onLayoutChange(this.mediaQueryLayoutVertical);
      this.mediaQueryLayoutVertical.addListener(this.onLayoutChange);
    }
  },

  beforeDestroy() {
    if (this.mediaQueryLayoutVertical) {
      this.mediaQueryLayoutVertical.removeListener(this.onLayoutChange);
    }
  },

  data() {
    return {
      navPanel: null,
      lastOpenPanel: null
    };
  },

  computed: {
    sessionStatus() {
      return this.$store.state.g2wSession.sessionStatus;
    },

    activePoll() {
      return this.$store.state.g2wSession.polls.active;
    },

    sharedPollResults() {
      return this.$store.state.g2wSession.polls.sharedResults;
    }
  },

  watch: {
    sessionStatus(newValue) {
      if (newValue === SESSION_STATUS.left || newValue === SESSION_STATUS.ended) {
        if (this.navPanel && this.navPanel.item !== 'info') {
          this.navPanel = navPanels.info;
        }
      }
    },

    activePoll(newValue) {
      this.autoPollsPanel(newValue);
    },

    sharedPollResults(newValue) {
      this.autoPollsPanel(newValue);
    },

    navPanel(newValue) {
      this.$emit('update:navPanel', newValue);
    }
  },

  methods: {
    clickNavItem({item}) {
      if (navPanels[item]) {
        if (this.navPanel && this.navPanel.item === item) {
          this.navPanel = null;
        } else {
          this.navPanel = navPanels[item];
          this.$telemetryTrack('panel open', {item});
        }
      }
      if (!this.navPanel || this.navPanel.item !== 'polls') {
        this.lastOpenPanel = this.navPanel; // when poll closes go back to last clicked or closed
      }

      if (item === 'exit') {
        this.$telemetryTrack('exit clicked');
        this.$emit('exit');
      }
    },

    autoPollsPanel(pollOrResults) {
      if (pollOrResults) {
        // poll or results start
        this.navPanel = navPanels.polls;
      } else if (this.navPanel === navPanels.polls) {
        this.navPanel = this.lastOpenPanel;
      }
    },

    onLayoutChange(mediaQueryList) {
      if (mediaQueryList.matches) {
        // layout just became vertical
        if (!this.navPanel) {
          this.navPanel = navPanels.info;
        }
      } else if (this.navPanel === navPanels.info) {
        // layout just became horizontal
        this.navPanel = null;
        this.lastOpenPanel = null;
      }
    }
  }
};

export default Navigation;
</script>

<style lang="sass">
  @import 'src/scss/variables'
  @import 'src/scss/mixins'

  .navigation
    display: flex
    @include layout-horizontal
      height: 100%
    @include layout-vertical
      flex-direction: column
      width: 100%

</style>
